import type { UploadFile } from 'antd';
import {
  formatDistanceToNow,
  formatDuration,
  intervalToDuration,
} from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { unionBy } from 'lodash-es';
import type { UploadFileOutputDto } from '../file/model';
import { toFileInputDtoList } from '../file/utils';
import type { ScriptDto } from '../script/script.dto';
import { TaskStatusCode, type VideoReplicationTaskDto } from '../task/model';
import { GenerationType } from '../video-model/model-spec';
import { ScriptSource } from '../video/model';
import { buildMultiPrompt } from '../video/utils';

export const MAX_VIDEO_REPLICATION_REFERENCE_IMAGES = 9;

export function formatVideoReplicationTaskElapsed(
  task: Pick<VideoReplicationTaskDto, 'created_at' | 'status' | 'updated_at'>,
) {
  const isSettled =
    task.status === TaskStatusCode.任务成功 ||
    task.status === TaskStatusCode.任务失败;

  if (!isSettled) {
    return `已进行 ${formatDistanceToNow(task.created_at, {
      locale: zhCN,
      includeSeconds: true,
    })}`;
  }

  return (
    formatDuration(
      intervalToDuration({
        start: task.created_at,
        end: task.updated_at ?? Date.now(),
      }),
      { locale: zhCN },
    ) || '0 秒'
  );
}

export function buildVideoReplicationPrompt(result: ScriptDto) {
  return (
    buildMultiPrompt(result.segments, result.extra_prompt) || result.prompt
  );
}

export function buildVideoReplicationVideoPageData(input: {
  id: string;
  result: ScriptDto;
  prompt: string;
  referenceImages: UploadFile<UploadFileOutputDto>[];
}): ScriptDto & {
  simple_mode: true;
  source: ScriptSource.爆款复刻 | undefined;
} {
  const aspectRatio = input.result.aspect_ratio || '9:16';

  return {
    ...input.result,
    id: input.id,
    prompt: input.prompt.trim(),
    file_list: unionBy(
      toFileInputDtoList(input.referenceImages),
      (item) => item.url,
    ),
    generation_type: GenerationType.Reference,
    aspect_ratio: aspectRatio,
    resolution: input.result.resolution || '720p',
    size:
      input.result.size || (aspectRatio === '16:9' ? '1280x720' : '720x1280'),
    avatar_ids: input.result.avatar_ids ?? [],
    simple_mode: true,
    source: ScriptSource.爆款复刻,
  };
}
